import { asWidgetProps, SCROLL_TO_ELEMENT_DEFAULT } from 'Src/types/widget-helpers';
import type { ComponentProps } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { constants } from '@clubmed/utils';
import { ICONS } from 'Components/utils/icon/icons';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { CriteriaTrigger } from 'Components/widgets/criteria/CriteriaTrigger';
import { FilterTransport } from 'Components/widgets/criteria/criteria-transport/filter-transport/FilterTransport';
import { useSyncDepartureCity } from 'Src/hooks/use-sync-departure-city/useSyncDepartureCity';
import { t } from 'Src/i18n';
import { changeDepartureCity } from 'Store/faceting/facetingSearch.action';
const { NO_TRANSPORT_VALUE } = constants;
export const TransportCriteria = ({
  departureCitiesOptions,
  departureCity,
  handleDepartureCity,
  initialTransport,
  isJoyview = false,
  transportIcon = ICONS.TRANSPORT,
  withValidDepartureCity,
}: {
  departureCitiesOptions: DepartureCities;
  departureCity: string;
  handleDepartureCity?: (arg0: any) => void;
  initialTransport?: string | null | undefined;
  isJoyview?: boolean;
  transportIcon?: string;
  withValidDepartureCity?: boolean;
}) => {
  const isInitialMount = useRef(true);
  const dispatch = useDispatch();
  const [isTransportCriteriaOpen, setTransportCriteriaIsOpen] = useState(false);
  const [isTransportCriteriaSet, setTransportCriteriaIsSet] = useState(false);
  // Store update effect
  const { inputDepartureCity, setInputDepartureCity } = useSyncDepartureCity({
    departureCitiesOptions,
    departureCity,
    initialTransport,
    onChange: (newDepartureCity: string) => {
      setTransportCriteriaIsSet(Boolean(newDepartureCity));
    },
    withValidDepartureCity,
  });
  const noDepartureCitiesOptions = [
    {
      isDisabled: true,
      isSelected: true,
      label: t('booking.priceCalendar.priceStatus.noFlight'),
      value: '',
    },
  ];
  useEffect(() => {
    if (!isInitialMount.current && inputDepartureCity) {
      saveTransport();
    }

    isInitialMount.current = false;
  }, [inputDepartureCity]);

  const saveTransport = () => {
    if (inputDepartureCity !== departureCity || !inputDepartureCity) {
      if (handleDepartureCity) {
        handleDepartureCity(inputDepartureCity || NO_TRANSPORT_VALUE);
      } else {
        dispatch(changeDepartureCity(inputDepartureCity || NO_TRANSPORT_VALUE));
      }
    }

    setTransportCriteriaIsOpen(false);
    setTransportCriteriaIsSet(Boolean(inputDepartureCity));
  };

  const getIcon = () => {
    if (!departureCity || departureCity === NO_TRANSPORT_VALUE) {
      return ICONS.NO_TRANSPORT;
    }

    return transportIcon;
  };

  const getLabel = () => {
    if (!isTransportCriteriaSet) {
      return t('faceting.transport.subTitle');
    }

    const valueToMatch = departureCity || NO_TRANSPORT_VALUE;
    const selectedOption = departureCitiesOptions.find((item) => item.value === valueToMatch);
    return selectedOption ? selectedOption.label : t('faceting.transport.subTitle');
  };

  const buttonLabel = <span className="block flex-1 truncate">{getLabel()}</span>;
  const onOpen = useCallback(() => {
    setTransportCriteriaIsOpen(true);
    scrollToElement(
      '[data-testid="TransportCriteria-trigger"]',
      500,
      '',
      SCROLL_TO_ELEMENT_DEFAULT,
    );
  }, []);
  const onClear = useCallback(() => {
    if (handleDepartureCity) {
      handleDepartureCity(NO_TRANSPORT_VALUE);
    } else {
      dispatch(changeDepartureCity(NO_TRANSPORT_VALUE));
    }

    setTransportCriteriaIsSet(false);
  }, []);
  return (
    <CriteriaTrigger
      dataTestId="TransportCriteria-trigger"
      buttonLabel={buttonLabel}
      buttonIcon={getIcon()}
      buttonIsFilled={Boolean(
        isTransportCriteriaSet && departureCity && departureCity !== NO_TRANSPORT_VALUE,
      )}
      buttons={[
        {
          children: t('faceting.reset'),
          onClick: () => {
            setInputDepartureCity('');
          },
          theme: 'link',
        },
        asWidgetProps({
          children: t('faceting.validate'),
          'data-testid': 'TransportCriteria-trigger-validate',
          onClick: saveTransport,
        }),
      ]}
      hasSwitch
      isJoyview={isJoyview}
      isOpen={isTransportCriteriaOpen}
      onClear={onClear}
      onClose={saveTransport}
      onOpen={onOpen}
      theme="default"
      trackingLabel="departureCity"
    >
      <FilterTransport
        {...asWidgetProps<ComponentProps<typeof FilterTransport>>({
          changeDepartureCity: (newDepartureCity: string) => {
            setInputDepartureCity(newDepartureCity);
          },
          className: 'h-full',
          dataTestId: 'TransportCriteria-input',
          departureCity: !departureCitiesOptions.length ? '' : inputDepartureCity,
          isDisabled: !departureCitiesOptions.length,
          labelDepartingFrom: t('faceting.transport.departingFrom'),
          optionsDepartureCities:
            departureCitiesOptions.length > 0 ? departureCitiesOptions : noDepartureCitiesOptions,
          placeholderFrom: t('faceting.transport.titleFrom'),
          selectPlaceholder: t('faceting.transport.selectPlaceholder'),
          validateDepartureCity: saveTransport,
        })}
      />
    </CriteriaTrigger>
  );
};
